import type { MouseEvent } from 'react'
import { useCallback, useMemo } from 'react'

import { SEARCH_TRACK_TAGS_FILTER_FIELD } from '@disco/constants/search'
import { setFilters } from '@disco/state-management/reducers/search-page'

import type { FilterState } from 'types/filter-page'

import { TAGS_FILTER_DEFINITION } from 'components/search/search-filters/filter-definitions'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { ColoredTag } from './indicator-colors'

type IndicatorTags = {
  activeIds: ReadonlySet<number>
  orderedTags: ColoredTag[]
  handleTagClick: (event: MouseEvent, tagId: number) => void
}

// Filter bar adds "Tags" with no value, so match on field or we'd add a second.
const isTagsFilter = (filter: FilterState): boolean =>
  filter.definition.field === SEARCH_TRACK_TAGS_FILTER_FIELD

export const useIndicatorTags = (coloredTags: ColoredTag[]): IndicatorTags => {
  const dispatch = useDispatch()
  // Runs in every row, so take filters only; whole slice redraws on any change.
  const filters = useSelector(state => state.searchPage.filters)

  const tagsFilter = useMemo(() => filters.find(isTagsFilter), [filters])

  const activeIds = useMemo<ReadonlySet<number>>(
    () => new Set(Array.isArray(tagsFilter?.value) ? tagsFilter.value : []),
    [tagsFilter],
  )

  // Active dots lead so they line up down the column, never hidden in overflow.
  const orderedTags = useMemo(() => {
    if (activeIds.size === 0) return coloredTags
    return [
      ...coloredTags.filter(tag => activeIds.has(tag.id)),
      ...coloredTags.filter(tag => !activeIds.has(tag.id)),
    ]
  }, [activeIds, coloredTags])

  // Without this, clicking a dot would also select the track.
  const handleTagClick = useCallback(
    (event: MouseEvent, tagId: number) => {
      event.stopPropagation()

      const nextTagIds = activeIds.has(tagId)
        ? [...activeIds].filter(id => id !== tagId)
        : [...activeIds, tagId]

      // Empty tags filter matches everything, so drop it with its last tag.
      if (nextTagIds.length === 0) {
        dispatch(setFilters(filters.filter(filter => !isTagsFilter(filter))))
        return
      }

      // Keeps the id so the badge stays mounted, plus any operator already chosen.
      const nextFilter: FilterState = {
        ...(tagsFilter ?? { definition: TAGS_FILTER_DEFINITION }),
        value: nextTagIds,
        operator: tagsFilter?.operator ?? 'equals',
      }

      dispatch(
        setFilters(
          tagsFilter
            ? filters.map(filter =>
                isTagsFilter(filter) ? nextFilter : filter,
              )
            : [...filters, nextFilter],
        ),
      )
    },
    [activeIds, dispatch, filters, tagsFilter],
  )

  return { activeIds, orderedTags, handleTagClick }
}
